import { useEffect, useRef, useState } from 'react';
import './App.css';
import { createIdempotencyKey, getRsvpFormToken, submitRsvp } from './rsvpApi';

const localeByLanguage = { af: 'af-ZA', en: 'en-ZA' };
const accommodationCodes = ['friday', 'saturday', 'weekend', 'own'];

const copy = {
  af: {
    kicker: 'Ons trou',
    intro: 'Ons sal bly wees as julle saam met ons hierdie naweek kan vier. Laat weet ons gerus hier onder of julle dit kan maak.',
    date: 'Datum', time: 'Tyd', venue: 'Plek', saturday: 'Saterdag', timeNote: 'vir 14:00',
    stayTitle: 'Verblyf',
    stayIntro: 'Verblyf is by SeMoer beskikbaar as julle wil oorslaap. Pryse is per persoon en sluit die etes hier onder in.',
    stayIntro2: 'Die bespreekings by SeMoer word slegs deur die bruidspaar gedoen.',
    stayDetails: 'Die troue is ’n naweektroue wat Vrydagaand begin. Saterdagmiddag is die seremonie.',
    rules: 'Reëls vir die naweek',
    dayRules: ['Geen kinders word toegelaat nie.', 'Semi-formele drag. Geen denims asseblief.', 'Vermy sagte ligte pienk, mint en wit.', 'RSVP voor 28 November 2026.'],
    payNote: 'Besprekings word saam met jou RSVP gedoen. Ons stuur bankbesonderhede en ’n verwysingsnommer sodra jy vir verblyf RSVP het. Betaling moet teen einde Februarie 2027 gedoen wees.',
    deadline: 'RSVP voor 28 Nov', name: 'Naam en van', person: 'Persoon', email: 'E-posadres', phone: 'Selnommer', guests: 'Hoeveel mense kom?',
    attend: 'Kan julle dit maak?', yes: 'Ja, ons kom', no: 'Nee, ongelukkig nie',
    stayChoice: 'Jou keuse van verblyf', emptyStay: 'Nog niks gekies nie. Kies een van die opsies hier bo.',
    pricePerPerson: 'Prys per persoon', totalPrice: 'Totale prys', noVenueCharge: 'Geen verblyfkoste', self: 'Self',
    notes: 'Dieetvereistes of ’n boodskap', send: 'Stuur RSVP', sending: 'Stuur tans…',
    requiredNote: 'Velde gemerk met * is verpligtend.', required: 'Verpligtend',
    validationTitle: 'Vul asseblief die gemerkte velde in:',
    guestCountRequired: 'Vul die aantal mense in (tussen 1 en 10).',
    nameRequired: 'Vul asseblief hierdie persoon se naam en van in.',
    emailRequired: 'Vul asseblief jou e-posadres in.', emailInvalid: 'Vul asseblief ’n geldige e-posadres in.',
    phoneRequired: 'Vul asseblief jou selnommer in.',
    attendanceRequired: 'Kies asseblief of julle die troue kan bywoon.',
    submitError: 'Ons kon nie jou RSVP stuur nie. Jou besonderhede is behou; probeer asseblief weer.',
    formNote: 'Ons antwoord binne ’n paar dae met bankbesonderhede indien jy verblyf gekies het.',
    thanks: 'Dankie', thanksTitle: 'Ons het jou RSVP',
    thanksBody: 'Baie dankie! Ons het jou antwoord ontvang. As jy verblyf gekies het, stuur ons bankbesonderhede met ’n verwysingsnommer per e-pos.',
    again: 'Nog ’n RSVP',
    stays: [
      ['Vrydagaand', 100000, 'Sluit Vrydagaand se ete en Saterdagoggend se ontbyt in.'],
      ['Saterdagaand', 90000, 'Sluit Sondagoggend se ontbyt in.'],
      ['Die hele naweek', 190000, 'Vrydag- en Saterdagaand, met 3 etes per persoon.'],
      ['Eie verblyf', null, 'Ons reël ons eie verblyf buite SeMoer.'],
    ],
  },
  en: {
    kicker: 'We are getting married',
    intro: 'We would love for you to celebrate this day with us. Please let us know below whether you can join.',
    date: 'Date', time: 'Time', venue: 'Venue', saturday: 'Saturday', timeNote: 'for 14:00',
    stayTitle: 'Accommodation',
    stayDetails: 'The wedding is a weekend celebration that starts on Friday evening. The ceremony is on Saturday afternoon.',
    rules: 'Rules for the day',
    dayRules: ['This is an adults-only celebration — no children are allowed.', 'Semi-formal attire. No denim please.', 'Please avoid soft light pink, mint and white.', 'RSVP before 28 November 2026.'],
    stayIntro: 'Accommodation is available at SeMoer if you would like to stay over. Prices are per person and include the meals listed below.',
    stayIntro2: 'Bookings at SeMoer are made only by the bridal couple.',
    payNote: 'Bookings are made together with your RSVP. We will send banking details and a reference number once you RSVP for accommodation. Payment is due by the end of February 2027.',
    deadline: 'RSVP by 28 Nov', name: 'Full name', person: 'Person', email: 'Email address', phone: 'Mobile number', guests: 'How many people are coming?',
    attend: 'Can you join us?', yes: 'Yes, we will be there', no: 'Sadly, we cannot',
    stayChoice: 'Your accommodation choice', emptyStay: 'Nothing selected yet. Choose one of the options above.',
    pricePerPerson: 'Price per person', totalPrice: 'Total price', noVenueCharge: 'No venue charge', self: 'Self',
    notes: 'Dietary needs or a message', send: 'Send RSVP', sending: 'Sending…',
    requiredNote: 'Fields marked with * are required.', required: 'Required',
    validationTitle: 'Please complete the highlighted fields:',
    guestCountRequired: 'Enter the number of people (between 1 and 10).',
    nameRequired: 'Please enter this person’s full name.',
    emailRequired: 'Please enter your email address.', emailInvalid: 'Please enter a valid email address.',
    phoneRequired: 'Please enter your mobile number.',
    attendanceRequired: 'Please choose whether you can attend the wedding.',
    submitError: 'We could not send your RSVP. Your details have been kept; please try again.',
    formNote: 'We will reply within a few days with banking details if you selected accommodation.',
    thanks: 'Thank you', thanksTitle: 'Your RSVP is in',
    thanksBody: 'Thank you! We have your reply. If you chose accommodation, we will email banking details with a reference number.',
    again: 'Another RSVP',
    stays: [
      ['Friday night', 100000, 'Includes Friday evening dinner and Saturday breakfast.'],
      ['Saturday night', 90000, 'Includes Sunday morning breakfast.'],
      ['The whole weekend', 190000, 'Friday and Saturday night, with 3 meals per person.'],
      ['Own accommodation', null, 'We are arranging our own accommodation off-site.'],
    ],
  },
};

const initialForm = { names: ['', ''], email: '', phone: '', guests: 2, attend: '', notes: '', website: '' };

function CornerMarks({ subtle = false }) {
  return <>{['tl', 'tr', 'bl', 'br'].map((corner) => <i className={`corner corner-${corner}${subtle ? ' corner-subtle' : ''}`} key={corner} aria-hidden="true">+</i>)}</>;
}

function App() {
  const [language, setLanguage] = useState('af');
  const [stay, setStay] = useState(-1);
  const [form, setForm] = useState(initialForm);
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [formToken, setFormToken] = useState('');
  const cardRef = useRef(null);
  const portraitRef = useRef(null);
  const idempotencyKeyRef = useRef(null);
  const t = copy[language];
  const formatMoney = (amountInCents) => new Intl.NumberFormat(localeByLanguage[language], {
    style: 'currency',
    currency: 'ZAR',
    maximumFractionDigits: 0,
  }).format(amountInCents / 100);

  useEffect(() => {
    document.documentElement.lang = localeByLanguage[language];
  }, [language]);

  useEffect(() => {
    let active = true;
    getRsvpFormToken()
      .then((token) => { if (active) setFormToken(token); })
      .catch(() => { if (active) setFormToken(''); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    const items = cardRef.current.querySelectorAll('[data-reveal]');
    const motion = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (typeof IntersectionObserver === 'undefined' || motion?.matches) {
      return undefined;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0, rootMargin: '0px 0px -24px' });
    items.forEach((item) => {
      if (!item.classList.contains('is-visible')) {
        item.classList.add('reveal-ready');
        observer.observe(item);
      }
    });
    const showFocusedContent = (event) => {
      let section = event.target.closest('[data-reveal]');
      while (section) {
        section.classList.add('is-visible');
        section = section.parentElement?.closest('[data-reveal]');
      }
    };
    const revealAll = () => {
      if (motion.matches) items.forEach((item) => item.classList.add('is-visible'));
    };
    const card = cardRef.current;
    card.addEventListener('focusin', showFocusedContent);
    motion?.addEventListener?.('change', revealAll);
    return () => {
      observer.disconnect();
      items.forEach((item) => item.classList.remove('reveal-ready'));
      card.removeEventListener('focusin', showFocusedContent);
      motion?.removeEventListener?.('change', revealAll);
    };
  }, [sent, language]);

  useEffect(() => {
    const motion = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    let frame = 0;
    const updatePortrait = () => {
      frame = 0;
      const portrait = portraitRef.current;
      if (!portrait) return;
      if (motion?.matches) {
        portrait.style.setProperty('--portrait-shift', '0px');
        return;
      }
      const bounds = portrait.getBoundingClientRect();
      if (bounds.bottom < 0 || bounds.top > window.innerHeight) return;
      const progress = (window.innerHeight / 2 - bounds.top - bounds.height / 2) / (window.innerHeight + bounds.height);
      portrait.style.setProperty('--portrait-shift', `${Math.max(-12, Math.min(12, progress * 24))}px`);
    };
    const scheduleUpdate = () => {
      if (!frame) frame = requestAnimationFrame(updatePortrait);
    };
    scheduleUpdate();
    window.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('resize', scheduleUpdate);
    motion?.addEventListener?.('change', scheduleUpdate);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', scheduleUpdate);
      window.removeEventListener('resize', scheduleUpdate);
      motion?.removeEventListener?.('change', scheduleUpdate);
    };
  }, []);

  const clearValidationError = (field) => {
    setValidationErrors((current) => {
      if (!current[field]) return current;
      const next = { ...current };
      delete next[field];
      return next;
    });
  };
  const update = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
    clearValidationError(field);
  };
  const updateGuestCount = (event) => {
    const rawValue = event.target.value;
    const guests = rawValue === '' ? '' : Math.min(10, Math.max(1, Number(rawValue)));
    setForm((current) => ({
      ...current,
      guests,
      names: guests === '' ? current.names : Array.from({ length: guests }, (_, index) => current.names[index] || ''),
    }));
    clearValidationError('guests');
  };
  const updateGuestName = (index) => (event) => {
    setForm((current) => ({
      ...current,
      names: current.names.map((name, nameIndex) => nameIndex === index ? event.target.value : name),
    }));
    clearValidationError(`name-${index}`);
  };
  const pickAttendance = (value) => {
    setForm((current) => ({ ...current, attend: value }));
    setSubmitError('');
    clearValidationError('attend');
    if (value === 'no') setStay(-1);
  };
  const submit = async (event) => {
    event.preventDefault();
    const errors = {};
    const guestCount = Number(form.guests);
    if (!Number.isInteger(guestCount) || guestCount < 1 || guestCount > 10) errors.guests = 'guestCountRequired';
    form.names.forEach((name, index) => {
      if (!name.trim()) errors[`name-${index}`] = 'nameRequired';
    });
    if (!form.email.trim()) errors.email = 'emailRequired';
    else if (!event.currentTarget.elements.email.validity.valid) errors.email = 'emailInvalid';
    if (!form.phone.trim()) errors.phone = 'phoneRequired';
    if (!form.attend) errors.attend = 'attendanceRequired';
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setSubmitError('');
      const formElement = event.currentTarget;
      requestAnimationFrame(() => formElement.querySelector('[aria-invalid="true"]')?.focus());
      return;
    }
    setSubmitting(true);
    setSubmitError('');
    if (!idempotencyKeyRef.current) idempotencyKeyRef.current = createIdempotencyKey();
    try {
      await submitRsvp({
        guestCount,
        guestNames: form.names,
        email: form.email,
        phone: form.phone,
        attendance: form.attend,
        accommodation: form.attend === 'yes' && stay >= 0 ? accommodationCodes[stay] : null,
        notes: form.notes,
        locale: localeByLanguage[language],
        website: form.website,
      }, idempotencyKeyRef.current, formToken);
      setSent(true);
      requestAnimationFrame(() => cardRef.current?.scrollIntoView?.({ behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'end' }));
    } catch (_error) {
      setSubmitError(t.submitError);
    } finally {
      setSubmitting(false);
    }
  };
  const reset = () => {
    setSent(false);
    setStay(-1);
    setForm(initialForm);
    setSubmitError('');
    setValidationErrors({});
    idempotencyKeyRef.current = null;
    setFormToken('');
    getRsvpFormToken().then(setFormToken).catch(() => setFormToken(''));
  };

  const RequiredMark = () => <span className="required-mark" aria-hidden="true">*</span>;
  const validationItems = Object.entries(validationErrors).map(([field, messageKey]) => [field, t[messageKey]]);

  return (
    <main className="invitation-page">
      <div className="language-wrap">
        <div className="language-switch" aria-label="Language">
          {['af', 'en'].map((lang) => (
            <button className={language === lang ? 'active' : ''} type="button" onClick={() => setLanguage(lang)} aria-pressed={language === lang} key={lang}>{lang}</button>
          ))}
        </div>
      </div>

      <article className="invitation-card" ref={cardRef}>
        <CornerMarks />
        <header className="hero-copy" data-reveal>
          <div className="kicker"><span />{t.kicker}<span /></div>
          <h1>Heinrich &amp; Magda</h1>
          <p>{t.intro}</p>
        </header>

        <section className="portrait-frame" ref={portraitRef} data-reveal>
          <CornerMarks subtle />
          <img className="flower flower-top" src="/botanical-accent.png" alt="" />
          <img className="flower flower-bottom" src="/botanical-accent.png" alt="" />
          <div className="portrait-clip">
            <img className="portrait" src="/wedding-couple.jpg" alt="Heinrich and Magda" />
            <div className="portrait-tint" aria-hidden="true" />
          </div>
        </section>

        <section className="event-grid" aria-label="Wedding details" data-reveal>
          {[[t.date, '1 MEI 2027', t.saturday], [t.time, '13:30', t.timeNote], [t.venue, 'SeMoer', 'Potchefstroom']].map(([label, value, note]) => (
            <div className="event-detail" key={label}><span>{label}</span><strong>{value}</strong><small>{note}</small></div>
          ))}
        </section>

        <section className="section-block" data-reveal>
          <div className="section-heading"><h2>{t.stayTitle}</h2><span /></div>
          <p className="section-intro">{t.stayDetails}</p>
          <p className="section-intro">{t.stayIntro}</p>
          <p className="section-intro" style={{ fontWeight: 'bold' }}>{t.stayIntro2}</p>
          <div className="stay-grid">
            {t.stays.map(([label, priceInCents, description], index) => (
              <button className={`stay-card${stay === index ? ' selected' : ''}`} type="button" onClick={() => setStay(index)} aria-pressed={stay === index} key={label} data-reveal style={{ '--reveal-delay': `${index * 65}ms` }}>
                <span className="check" aria-hidden="true">{stay === index ? '✓' : ''}</span>
                <small>{label}</small><strong>{priceInCents === null ? t.self : formatMoney(priceInCents)}</strong><p>{description}</p>
              </button>
            ))}
          </div>
          <p className="fine-print">{t.payNote}</p>
        </section>

        <section className="section-block" data-reveal>
          <div className="section-heading"><h2>{t.rules}</h2><span /></div>
          <ul className="section-intro">{t.dayRules.map((rule) => <li key={rule}>{rule}</li>)}</ul>
        </section>

        {!sent ? (
          <form className="rsvp-form" onSubmit={submit} noValidate data-reveal>
            <div className="rsvp-honeypot" aria-hidden="true">
              <label htmlFor="rsvp-website">Website</label>
              <input id="rsvp-website" name="website" type="text" tabIndex="-1" autoComplete="off" value={form.website} onChange={update('website')} />
            </div>
            <div className="section-heading form-title"><h2>RSVP</h2><span /><em>{t.deadline}</em></div>
            <p className="required-note">{t.requiredNote}</p>
            {validationItems.length > 0 && (
              <div className="validation-summary" role="alert" aria-labelledby="validation-title">
                <strong id="validation-title">{t.validationTitle}</strong>
                <ul>{validationItems.map(([field, message]) => <li key={field}><a href={`#${field}`}>{message}</a></li>)}</ul>
              </div>
            )}
            <div className="field-grid">
              <label htmlFor="guests">
                <span>{t.guests} <RequiredMark /><span className="sr-only"> ({t.required})</span></span>
                <input id="guests" name="guestCount" type="number" inputMode="numeric" min="1" max="10" step="1" value={form.guests} onChange={updateGuestCount} required aria-invalid={Boolean(validationErrors.guests)} aria-describedby={validationErrors.guests ? 'guests-error' : undefined} />
                {validationErrors.guests && <span className="field-error" id="guests-error">{t[validationErrors.guests]}</span>}
              </label>
              {form.names.map((name, index) => (
                <label className="guest-name-field" htmlFor={`name-${index}`} key={index}>
                  <span>{t.name} — {t.person} {index + 1} <RequiredMark /><span className="sr-only"> ({t.required})</span></span>
                  <input id={`name-${index}`} type="text" value={name} onChange={updateGuestName(index)} required aria-invalid={Boolean(validationErrors[`name-${index}`])} aria-describedby={validationErrors[`name-${index}`] ? `name-${index}-error` : undefined} />
                  {validationErrors[`name-${index}`] && <span className="field-error" id={`name-${index}-error`}>{t[validationErrors[`name-${index}`]]}</span>}
                </label>
              ))}
              <label htmlFor="email">
                <span>{t.email} <RequiredMark /><span className="sr-only"> ({t.required})</span></span>
                <input id="email" name="email" type="email" value={form.email} onChange={update('email')} required aria-invalid={Boolean(validationErrors.email)} aria-describedby={validationErrors.email ? 'email-error' : undefined} />
                {validationErrors.email && <span className="field-error" id="email-error">{t[validationErrors.email]}</span>}
              </label>
              <label htmlFor="phone">
                <span>{t.phone} <RequiredMark /><span className="sr-only"> ({t.required})</span></span>
                <input id="phone" name="phone" type="tel" value={form.phone} onChange={update('phone')} required aria-invalid={Boolean(validationErrors.phone)} aria-describedby={validationErrors.phone ? 'phone-error' : undefined} />
                {validationErrors.phone && <span className="field-error" id="phone-error">{t[validationErrors.phone]}</span>}
              </label>
            </div>
            <fieldset id="attend" aria-required="true" aria-invalid={Boolean(validationErrors.attend)} aria-describedby={validationErrors.attend ? 'attend-error' : undefined}>
              <legend>{t.attend} <RequiredMark /><span className="sr-only"> ({t.required})</span></legend>
              <div className="attendance-options">
                {[['yes', t.yes], ['no', t.no]].map(([value, label]) => (
                  <button className={form.attend === value ? 'selected' : ''} type="button" onClick={() => pickAttendance(value)} aria-pressed={form.attend === value} key={value}>
                    <span aria-hidden="true">{form.attend === value ? '✓' : ''}</span>{label}
                  </button>
                ))}
              </div>
              {validationErrors.attend && <span className="field-error" id="attend-error">{t[validationErrors.attend]}</span>}
            </fieldset>
            {form.attend === 'yes' && (
              <div className="stay-summary">
                <span>{t.stayChoice}</span>
                {stay >= 0 ? (
                  <div className="stay-summary-details">
                    <p>{t.stays[stay][0]}</p>
                    <dl>
                      <div><dt>{t.pricePerPerson}</dt><dd>{t.stays[stay][1] === null ? t.noVenueCharge : formatMoney(t.stays[stay][1])}</dd></div>
                      <div><dt>{t.totalPrice}</dt><dd>{t.stays[stay][1] === null ? t.noVenueCharge : formatMoney(t.stays[stay][1] * form.guests)}</dd></div>
                    </dl>
                  </div>
                ) : <p>{t.emptyStay}</p>}
              </div>
            )}
            <label className="notes-field"><span>{t.notes}</span><textarea rows="3" value={form.notes} onChange={update('notes')} /></label>
            {submitError && <p className="submit-error" role="alert">{submitError}</p>}
            <div className="submit-row"><button type="submit" disabled={submitting || !formToken}>{submitting ? t.sending : t.send}</button><p>{t.formNote}</p></div>
          </form>
        ) : (
          <section className="thank-you" aria-live="polite">
            <span>{t.thanks}</span><h2>{t.thanksTitle}</h2><p>{t.thanksBody}</p><button type="button" onClick={reset}>{t.again}</button>
          </section>
        )}
      </article>
    </main>
  );
}

export default App;
